import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { get, post } from '../../utils/api';
import { GET_CONSENT_STATUS, GRANT_CONSENT } from '../../config/api';
import AuthContext from '../../context/AuthContext';

/*
 * Asks an existing member for data-sharing consent, once, on their way into the
 * app.
 *
 * New members have been asked at registration for a while, but nobody ever
 * asked the accounts that already existed, and there was no screen on which
 * they could have agreed even if they wanted to. This is that screen.
 *
 * NOT DISMISSABLE, and that is the point: no backdrop click, no Escape, no
 * close button. The only two ways out are Accept and Log out. It is also not
 * the enforcement — the server refuses requests independently
 * (middleware/consentGate.middleware.js), so a member who closes the tab and
 * comes back is asked again rather than slipping through.
 *
 * The wording is fetched, never hardcoded here. What matters if this is ever
 * questioned is what the person was actually shown, and that has to be the same
 * text the server is recording agreement to — so the server owns it and this
 * renders what it is handed.
 */
/*
 * Pages the dialog must never cover.
 *
 * Nobody can be asked to agree to a policy and then be prevented from reading
 * it — the dialog links straight to /privacy-policy, so covering that page
 * makes the link useless and the consent uninformed. Terms is here for the same
 * reason; the rest are the router's own "always accessible regardless of auth
 * state" set (routes/index.jsx), and none of them gives access to the product.
 *
 * This costs nothing in enforcement: these pages make no authenticated API
 * calls, and middleware/consentGate.middleware.js still refuses every request
 * that matters, so a member cannot use Talexo by sitting on the FAQ page.
 */
const CONSENT_EXEMPT_PATHS = [
    '/privacy-policy',
    '/terms-and-conditions',
    '/about-us',
    '/faqs',
    '/contact-us',
];

const ConsentGate = () => {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const { pathname } = useLocation();
    const onExemptPage = CONSENT_EXEMPT_PATHS.includes((pathname || '').replace(/\/+$/, '') || '/');

    const [notice, setNotice] = useState(null);
    const [policyVersion, setPolicyVersion] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const check = useCallback(async () => {
        try {
            const res = await get(GET_CONSENT_STATUS);
            if (res?.responseCode === 200 && res.responseData?.consentRequired) {
                setNotice(res.responseData.notice || null);
                setPolicyVersion(res.responseData.policyVersion || null);
            } else {
                setNotice(null);
            }
        } catch (err) {
            /*
             * Stay quiet and show nothing. A member who is not being asked must
             * never see this dialog because a status call failed, and one who IS
             * being asked will be stopped by the server anyway — at which point
             * the CONSENT_REQUIRED listener below opens the dialog.
             */
            setNotice(null);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            setNotice(null);
            return;
        }
        check();
    }, [isAuthenticated, check]);

    /*
     * Second way in: any API call refused with CONSENT_REQUIRED. This covers a
     * session that was already open when the gate was switched on, and is the
     * safety net if the status call above ever fails — without it a member could
     * end up with an app where nothing loads and nothing explains why.
     */
    useEffect(() => {
        const onRequired = () => { if (!notice) check(); };
        window.addEventListener('talexo:consent-required', onRequired);
        return () => window.removeEventListener('talexo:consent-required', onRequired);
    }, [notice, check]);

    // Escape must not close it. Registered only while the dialog is open.
    useEffect(() => {
        // Also skipped on an exempt page — otherwise the privacy policy would
        // be left unscrollable by a dialog that is not even on screen.
        if (!notice || onExemptPage) return undefined;
        const swallow = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
        window.addEventListener('keydown', swallow, true);
        // The page behind must not scroll while this is up.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', swallow, true);
            document.body.style.overflow = previousOverflow;
        };
    }, [notice, onExemptPage]);

    const accept = async () => {
        if (!agreed || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const res = await post(GRANT_CONSENT, { accepted: true, policyVersion });
            if (res?.responseCode === 200) {
                setNotice(null);
                // Anything that 403'd while the dialog was up needs re-fetching,
                // and a reload is honest about that rather than leaving half the
                // screen empty.
                window.location.reload();
                return;
            }
            setError(res?.responseMessage || 'That did not go through. Please try again.');
        } catch (err) {
            setError('That did not go through. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    /*
     * Hidden on an exempt page, NOT cleared. `notice` is kept so the dialog is
     * waiting the instant they navigate back — reading the policy must not be a
     * way to get rid of it.
     */
    if (!notice || onExemptPage) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-gate-title"
        >
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h2 id="consent-gate-title" className="text-lg font-semibold text-talexo-blue">
                        {notice.title}
                    </h2>
                </div>

                <div className="px-6 py-5">
                    {notice.summary && <p className="text-sm text-gray-600">{notice.summary}</p>}

                    {Array.isArray(notice.items) && notice.items.length > 0 && (
                        <ul className="mt-4 space-y-2">
                            {notice.items.map((item) => (
                                <li key={item} className="flex gap-2 text-sm text-gray-700">
                                    <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-talexo-blue" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <label className="mt-5 flex cursor-pointer select-none items-start gap-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => { setAgreed(e.target.checked); setError(''); }}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-talexo-blue focus:ring-talexo-blue/40"
                        />
                        <span>
                            {notice.statement}{' '}
                            <Link
                                to={notice.privacyPolicyPath || '/privacy-policy'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-talexo-blue underline hover:text-talexo-blue-dark"
                            >
                                Privacy Policy
                            </Link>
                        </span>
                    </label>

                    {notice.footNote && <p className="mt-3 text-xs text-gray-500">{notice.footNote}</p>}
                    {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end">
                    {/*
                      * Log out is the honest alternative to Accept. Offering a
                      * "not now" that let the member continue would make the
                      * whole dialog a suggestion, and the server would refuse
                      * every request anyway — so the app would simply appear
                      * broken.
                      */}
                    <button
                        type="button"
                        onClick={logout}
                        disabled={submitting}
                        className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                        Log out
                    </button>
                    <button
                        type="button"
                        onClick={accept}
                        disabled={!agreed || submitting}
                        className="rounded-md bg-talexo-blue px-4 py-2 text-sm font-semibold text-white hover:bg-talexo-blue-dark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {submitting ? 'Saving…' : 'Agree and continue'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentGate;
